// Two ways to draw a breakdown, each built for what it shows. Parts of a
// whole (where visits come from, which devices): a ring with each part's
// share beside it, or one bar with the biggest part's share over it in a
// short tile. A ranked list whose rows carry more numbers (pages, queries):
// rank, name and value with its bar, and the other numbers as columns beside
// it as the tile widens. Each takes its type size from its tile (plans.ts:
// splitFit, rankedFit) and sizes everything in em.

import { useState } from "react";
import { formatNumber } from "../../shared/format";
import { partsOf, rankedColumns, shareText, type Part } from "./breakdownData";
import { cx } from "../lib/util";
import { Linked } from "../lib/Linked";
import { useSize } from "../charts/charts";
import type { WP } from "./data";
import { quarter } from "./layout";
import { RANKED, rankedFit, splitFit } from "./plans";
import "../styles/breakdown.css";

export { rankedColumns };

const toneOf = (p: Part) => (p.tone === null ? "var(--fg-3)" : `var(--chart-${p.tone + 1})`);

/** Parts of a whole: a ring and its legend, or one bar with the legend under it. Hovering a part lights it everywhere. */
export function Split({ item, stream }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  const [hot, setHot] = useState<number | null>(null);
  // every part, always: a share is of the whole, so none is left out of it
  const items = s.items ?? [];
  const all = partsOf(items);
  if (!all.parts.length)
    return (
      <div className="empty">
        <span className="empty-title">Nothing counted in this period</span>
        <span className="empty-sub">{s.items?.length ? "Every part of it counted zero." : "Its source sent an empty list."}</span>
      </div>
    );
  const box = size.w ? size : { w: item.w - 28, h: item.h - 52 };
  const fit = splitFit(box, all.parts.length);
  // a ring draws a legend row for each of its parts, so a short one adds up more of the smallest as Other
  const { parts, total } = fit.id === "ring" && fit.rows < all.parts.length ? partsOf(items, fit.rows) : all;
  const lead = parts[0];
  const shown = fit.id === "ring" ? parts : parts.slice(0, fit.rows);
  const fmt = s.format ?? null;
  const legend = (
    <ul className={cx("sp-legend", fit.cols === 2 && "is-two")} onPointerLeave={() => setHot(null)}>
      {shown.map((p, i) => (
        <li key={p.label} className={cx(hot !== null && hot !== i && "is-dim")} onPointerEnter={() => setHot(i)}>
          <i style={{ background: toneOf(p) }} />
          <span className="sp-name">{p.label}</span>
          <span className="sp-n">{formatNumber(p.v, fmt)}</span>
          <b>{shareText(p.v, total)}</b>
        </li>
      ))}
    </ul>
  );
  if (fit.id === "ring") {
    // each part an arc of the ring, a hair apart, starting at the top and going round clockwise
    const gap = parts.length > 1 ? 1.1 : 0;
    let at = 0;
    const arcs = parts.map((p, i) => {
      const len = (p.v / total) * 100;
      const a = { i, p, from: at, len: Math.max(0.35, len - gap) };
      at += len;
      return a;
    });
    const focus = hot !== null ? parts[hot] : lead;
    return (
      <div className="sp is-ring" ref={ref} style={{ fontSize: quarter(fit.px) }}>
        <div className="sp-ring" onPointerLeave={() => setHot(null)}>
          <svg viewBox="0 0 100 100" aria-hidden>
            <circle className="sp-track" cx="50" cy="50" r="42" />
            {arcs.map((a) => (
              <circle
                key={a.p.label}
                className={cx("sp-arc", hot !== null && hot !== a.i && "is-dim")}
                cx="50"
                cy="50"
                r="42"
                pathLength={100}
                stroke={toneOf(a.p)}
                strokeDasharray={`${a.len} ${100 - a.len}`}
                strokeDashoffset={25 - a.from}
                onPointerEnter={() => setHot(a.i)}
              />
            ))}
          </svg>
          <div className="sp-mid">
            <b>{shareText(focus.v, total)}</b>
            <span>{focus.label}</span>
          </div>
        </div>
        {legend}
      </div>
    );
  }
  return (
    <div className="sp is-bar" ref={ref} style={{ fontSize: quarter(fit.px) }}>
      <div className="sp-head">
        <b>{shareText(lead.v, total)}</b>
        <span>{lead.label}</span>
      </div>
      <div className="sp-bar" onPointerLeave={() => setHot(null)}>
        {parts.map((p, i) => (
          <i key={p.label} className={cx(hot !== null && hot !== i && "is-dim")} style={{ flexGrow: p.v, background: toneOf(p) }} title={`${p.label}: ${shareText(p.v, total)}`} onPointerEnter={() => setHot(i)} />
        ))}
      </div>
      {fit.rows > 0 && legend}
    </div>
  );
}

// the extra columns, the most telling first: a query's position before its click-through rate, then its impressions
const FIRST = ["Position", "CTR", "Users", "Impressions"];

/** A ranked list with its columns: rank, name and value over its bar, and the other numbers beside it as room allows. */
export function Ranked({ item, stream }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  const cols = rankedColumns(s)!;
  const items = (s.items ?? []).slice(0, Number(item.props.max ?? 50) || 50);
  const rows = s.rows ?? [];
  const extras = cols.slice(2).sort((a, b) => (FIRST.indexOf(a) + 99) % 99 - (FIRST.indexOf(b) + 99) % 99);
  const fit = rankedFit(size.w ? size : { w: item.w - 28, h: item.h - 52 }, extras.length);
  // in the order the data gives them, those that fit
  const show = cols.slice(2).filter((c) => extras.slice(0, fit.columns).includes(c));
  const max = Math.max(0, ...items.map((i) => (typeof i.value === "number" ? i.value : 0)));
  const numbered = !!item.props.numbered;
  const cell = (v: unknown) => (typeof v === "number" ? formatNumber(v, null) : v === null || v === undefined ? "" : String(v));
  if (!items.length)
    return (
      <div className="empty">
        <span className="empty-title">Nothing ranked in this period</span>
      </div>
    );
  return (
    <div className={cx("rk", numbered && "is-numbered")} ref={ref} style={{ fontSize: quarter(fit.px), ["--rk-cols" as string]: `${numbered ? `${RANKED.rank}em ` : ""}minmax(0, 1fr) ${show.map(() => `${RANKED.column}em`).join(" ")} ${RANKED.value}em` }} data-scroll>
      <div className="rk-head">
        <span className="rk-name">{cols[0]}</span>
        {show.map((c) => (
          <span key={c} className="rk-col">
            {c}
          </span>
        ))}
        <span className="rk-v">{cols[1]}</span>
      </div>
      {items.map((it, i) => {
        const v = typeof it.value === "number" ? it.value : null;
        const inner = (
          <>
            {numbered && <span className="rk-rank">{i + 1}</span>}
            <span className="rk-name">
              {/* a row that is a link already opens as one; in any other, a web address in the name opens with a click */}
              <span className="rk-label">{it.href ? it.label : <Linked text={it.label} />}</span>
              {item.props.bars !== false && (
                <span className="rk-track">
                  <i style={{ width: `${v !== null && max > 0 ? Math.max(1.5, (v / max) * 100) : 0}%` }} />
                </span>
              )}
            </span>
            {show.map((c) => (
              <span key={c} className="rk-col">
                {cell(rows[i]?.[c])}
              </span>
            ))}
            <span className="rk-v">{v !== null ? formatNumber(v, s.format ?? null) : cell(it.value)}</span>
          </>
        );
        return it.href ? (
          <a key={i} className="rk-row" href={it.href} target="_blank" rel="noreferrer noopener" title={it.href} data-interactive>
            {inner}
          </a>
        ) : (
          <div key={i} className="rk-row">
            {inner}
          </div>
        );
      })}
    </div>
  );
}
